import type { CloudConfig } from '../../../common/constants/filters/cloud-config/types';
import type { SelectFilterState } from '../../../common/constants/filters/select-filter/types';
import {
	isProductKey,
	type PrimaryProductKey,
	type ProductKeys,
} from '../../../common/constants/product-keys';
import { SearchExperience, type SearchStoreExperience } from '../../../common/constants/search';

export const isSelectFilter = (
	type?: SelectFilterState<string>['type'] | string,
): type is SelectFilterState<string>['type'] => {
	return type === 'single-select' || type === 'multi-select';
};

// Each filter option is tied to a specific product because filters can be shared across products but
// contain different filter options. This is the case for Confluence Spaces and Confluence DC Spaces.
export const getProductForFilterOption = (params: {
	config: CloudConfig;
	isUniversal: boolean;
}): string => {
	if (params.isUniversal) {
		return 'all';
	}

	return (
		// If a product is selected, the filter option will be tied to the selected product
		params.config.product ??
		// If no product is selected, such as the 'all' state it will use the primary product. This is the case for suggested space filters.
		// All suggested filters are sets the primary product as the product selected after a filter option selection.
		params.config.primaryProduct ??
		'all' // config.primaryProduct should always be defined but 'all' is used as a catch-all
	);
};

export const getRelevantProductsForUniversalFilters = (params: {
	selectedProducts: ProductKeys[];
	primaryProduct?: PrimaryProductKey;
	experience: SearchStoreExperience;
	availableProducts: ProductKeys[];
}): ProductKeys[] => {
	if (params.selectedProducts.length > 0) {
		return params.selectedProducts;
	}

	const primaryProduct = params.primaryProduct ?? '';
	const safePrimaryProduct = isProductKey(primaryProduct) ? [primaryProduct] : undefined;

	if (params.experience === SearchExperience.QUICK_FIND) {
		return safePrimaryProduct ?? params.availableProducts;
	}

	return params.availableProducts;
};
